import { useEffect, useRef } from 'react';
import { useLang } from '../context/LangContext';

const BRANDS = ['Shivali', 'S4U', 'Sayuri', 'Lapink', 'Aashirwad', 'Banarasi', 'Kimora', 'Bonny'];

export default function Heritage() {
  const { t } = useLang();
  const quoteRef   = useRef(null);
  const sectionRef = useRef(null);

  // Scroll reveal for section
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.querySelector('.reveal')?.classList.add('visible'); obs.unobserve(el); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Word-by-word scroll lighting (epic-design text-animations)
  useEffect(() => {
    const el = quoteRef.current;
    if (!el) return;

    const text  = el.textContent;
    const words = text.split(' ');
    el.innerHTML = words.map(w => `<span>${w} </span>`).join('');
    const spans = el.querySelectorAll('span');

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          spans.forEach((sp, i) => {
            setTimeout(() => sp.classList.add('lit'), i * 80);
          });
          obs.unobserve(el);
        }
      });
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="heritage"
      ref={sectionRef}
      style={{ padding: '10rem 0', position: 'relative', overflow: 'hidden' }}
    >
      {/* Background liquid blob */}
      <div style={{
        position: 'absolute', width: 'clamp(400px, 100vw, 800px)', height: 'clamp(400px, 100vw, 800px)',
        background: 'radial-gradient(ellipse, rgba(183,110,121,0.06) 0%, transparent 70%)',
        top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
        animation: 'liquidBlob 16s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      <div className="container" style={{ maxWidth: '880px', textAlign: 'center', position: 'relative', zIndex: 2 }}>
        <div className="reveal" style={{ padding: '3rem 2rem' }}>
          {/* Ornament */}
          <div style={{ fontSize: '1.5rem', color: 'var(--accent)', marginBottom: '2rem', letterSpacing: '1rem', opacity: 0.7 }}>
            ✦ ✦ ✦
          </div>

          {/* Word-lit quote */}
          <h2
            className="heading-display word-light"
            ref={quoteRef}
            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', maxWidth: '700px', margin: '0 auto' }}
          >
            {t.heritageQuote}
          </h2>

          <p className="text-body" style={{ marginTop: '2rem', maxWidth: '580px', margin: '2rem auto 0' }}>
            {t.heritageDesc}
          </p>

          {/* Brand chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center', marginTop: '3rem' }}>
            {BRANDS.map((b, i) => (
              <span
                key={b}
                className="badge"
                style={{
                  animationDelay: `${i * 0.06}s`,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'default',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(183,110,121,0.2)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {b}
              </span>
            ))}
          </div>

          {/* Bottom ornament */}
          <div style={{ fontSize: '1.5rem', color: 'var(--accent)', marginTop: '3rem', letterSpacing: '1rem', opacity: 0.7 }}>
            ✦ ✦ ✦
          </div>
        </div>
      </div>
    </section>
  );
}
